import {
  Avatar, Box, Chip, Fade, FormControl, IconButton, MenuItem, Select,
  Switch, Tooltip, Typography, TextField,
} from '@mui/material';
import { DeleteOutline, PeopleAlt, CheckCircle, Cancel } from '@mui/icons-material';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchUserPhoto } from '../../services/graphService';
import type { UserRecord } from './adminTypes';
import { WORK_GROUP_OPTIONS } from '../../constants/scheduleEnums';
import { ROLE_OPTIONS, roleOption, ELEVATED_ROLE_VALUES } from '../../constants/roles';

// Accent for the Moftaki AI toggle (no semantic theme token for it yet).
const AI_ACCENT = '#7C4DFF';

// One source of truth for the row's grid template — header and body share it so
// the columns can never drift out of sync.
// User | Role | Work Group | Job Class | Flags(Assign·AI·Active) | Last Login | Delete
// (Tier moved to Schedule → Skill Matrix — it's a scheduling attribute, RBAC audit.)
const GRID_COLS = 'minmax(170px, 1.5fr) 130px 118px minmax(120px, 1.2fr) 128px 78px 28px';

const formatDate = (value: string | null): string => {
  if (!value) return 'Never';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

interface UserTableProps {
  users: UserRecord[];
  /** When false, the Lab Admin / Super Admin role options are disabled (only a Super Admin may grant them). */
  canAssignElevatedRoles?: boolean;
  onRoleChange?: (key: string, role: string) => void;
  onToggleStatus?: (key: string, current: string) => void;
  onDelete?: (user: UserRecord) => void;
  onToggleAssignable?: (key: string, current: boolean) => void;
  onToggleChatAccess?: (key: string, current: boolean) => void;
  /** Callback for scheduling profile field changes (Tier is edited on Schedule → Skill Matrix). */
  onSchedulingProfileChange?: (key: string, field: 'jobClass' | 'workGroup', value: string | null) => void;
}

export const UserTable = ({
  users, canAssignElevatedRoles = false, onRoleChange, onToggleStatus, onDelete,
  onToggleAssignable, onToggleChatAccess, onSchedulingProfileChange,
}: UserTableProps) => {
  const theme = useTheme();
  const { instance } = useMsal();
  const [photos, setPhotos] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (users.length === 0) return;
    let cancelled = false;
    const load = async () => {
      for (const user of users) {
        if (photos[user.Email]) continue;
        const url = await fetchUserPhoto(instance as any, user.Email);
        if (cancelled) return;
        setPhotos((prev) => ({ ...prev, [user.Email]: url }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [users, instance]); // eslint-disable-line react-hooks/exhaustive-deps

  if (users.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <PeopleAlt sx={{ fontSize: 40, color: alpha(theme.palette.text.primary, 0.12), mb: 1 }} />
        <Typography color="text.secondary">No users yet</Typography>
      </Box>
    );
  }

  const editable = !!onRoleChange;
  const headerSx = { fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 } as const;
  // Left divider marking the start of a column cluster (Staffing, then Flags).
  const clusterEdge = { borderLeft: `1px solid ${alpha(theme.palette.divider, 0.4)}`, pl: 1 } as const;
  const placeholderSx = { color: theme.palette.text.disabled, fontSize: '0.75rem' };

  return (
    <Box sx={{ minWidth: 0, overflowX: 'auto' }}>
      {/* Group band — makes the two RBAC axes visually explicit (RBAC.md):
          the access Role gates permissions; the scheduling profile never does. */}
      {editable && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: GRID_COLS,
          gap: 1, px: 2.5, pt: 0.75, pb: 0.25, alignItems: 'end', minWidth: 880,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        }}>
          <Box />
          <Tooltip title="The access role is the only thing that gates permissions (see RBAC.md)">
            <Typography sx={{ ...headerSx, fontSize: '0.6rem', color: theme.palette.primary.main }}>Access</Typography>
          </Tooltip>
          <Tooltip title="Feeds scheduling skills-matching and display grouping only — never permissions (see RBAC.md). Tier is managed on Schedule → Skill Matrix.">
            <Typography sx={{ ...headerSx, fontSize: '0.6rem', color: theme.palette.primary.main, gridColumn: 'span 2', borderLeft: `1px solid ${alpha(theme.palette.divider, 0.4)}`, pl: 1 }}>
              Scheduling profile
            </Typography>
          </Tooltip>
          <Typography sx={{ ...headerSx, fontSize: '0.6rem', color: theme.palette.primary.main, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.4)}`, pl: 1 }}>
            Account
          </Typography>
          <Box sx={{ gridColumn: 'span 2' }} />
        </Box>
      )}

      {/* Header row */}
      {editable && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: GRID_COLS,
          gap: 1, px: 2.5, py: 0.75, alignItems: 'center', minWidth: 880,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        }}>
          <Typography sx={headerSx}>User</Typography>
          <Typography sx={headerSx}>Role</Typography>
          <Typography sx={{ ...headerSx, ...clusterEdge }}>Work Group</Typography>
          <Typography sx={headerSx}>Job Class</Typography>
          <Box sx={{ ...clusterEdge, display: 'flex', justifyContent: 'space-around', gap: 0.5 }}>
            <Tooltip title="Visible in Tested By / Reviewed By / Approved By dropdowns"><Typography sx={headerSx}>Assign</Typography></Tooltip>
            <Tooltip title="Moftaki AI access"><Typography sx={headerSx}>AI</Typography></Tooltip>
            <Typography sx={headerSx}>Active</Typography>
          </Box>
          <Typography sx={{ ...headerSx, textAlign: 'right' }}>Last Login</Typography>
          <Box />
        </Box>
      )}

      {users.map((user, idx) => {
        const isAssignable = user.IsAssignable !== false && user.IsAssignable !== 0;
        const hasChat = !!(user as any).CanAccessChat;
        const isActive = user.Status === 'active';
        const roleOpt = roleOption(user.Role);

        return (
          <Fade in key={user.AppUserKey} timeout={150 + idx * 30}>
            <Box sx={{
              display: 'grid', gridTemplateColumns: GRID_COLS,
              gap: 1, px: 2.5, py: 0.75, alignItems: 'center', minWidth: 880,
              borderBottom: `1px solid ${alpha(theme.border.color, 0.2)}`,
              transition: 'background-color 0.15s',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) },
              '&:last-child': { borderBottom: 'none' },
            }}>
              {/* User info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Avatar src={photos[user.Email] ?? undefined} sx={{
                  width: 34, height: 34, fontSize: '0.82rem', fontWeight: 600,
                  bgcolor: alpha(roleOpt?.color || theme.palette.text.disabled, 0.12),
                  color: roleOpt?.color || theme.palette.text.secondary,
                }}>
                  {user.DisplayName?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.3 }} noWrap>
                    {user.DisplayName || user.Email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }} noWrap>
                    {user.Email}
                  </Typography>
                </Box>
              </Box>

              {/* Role */}
              {onRoleChange ? (
                <FormControl size="small">
                  <Select value={user.Role} onChange={(e) => onRoleChange(user.AppUserKey, e.target.value)}
                    sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: 0.5, pl: 1 } }}>
                    {ROLE_OPTIONS.map((r) => (
                      <MenuItem
                        key={r.value}
                        value={r.value}
                        disabled={!canAssignElevatedRoles && ELEVATED_ROLE_VALUES.includes(r.value) && r.value !== user.Role}
                        sx={{ fontSize: '0.78rem' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: r.color, flexShrink: 0 }} />
                          {r.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip label={roleOpt?.label || user.Role} size="small" variant="outlined"
                  sx={{ fontSize: '0.72rem', borderColor: roleOpt?.color, color: roleOpt?.color }} />
              )}

              {/* Work Group — start of the Staffing cluster */}
              <Box sx={clusterEdge}>
                {onSchedulingProfileChange ? (
                  <FormControl size="small" fullWidth>
                    <Select
                      displayEmpty
                      value={user.WorkGroup ?? ''}
                      onChange={(e) => onSchedulingProfileChange(user.AppUserKey, 'workGroup', e.target.value || null)}
                      renderValue={(v) => v || <Box component="span" sx={placeholderSx}>Group</Box>}
                      sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.4, pl: 1 } }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>— None —</MenuItem>
                      {WORK_GROUP_OPTIONS.map((wg) => (
                        <MenuItem key={wg} value={wg} sx={{ fontSize: '0.75rem' }}>{wg}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{user.WorkGroup || '—'}</Typography>
                )}
              </Box>

              {/* Job Class (free-text, from GAL) */}
              {onSchedulingProfileChange ? (
                <TextField
                  key={user.JobClass ?? ''}
                  placeholder="Job Class"
                  size="small"
                  fullWidth
                  defaultValue={user.JobClass ?? ''}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    const current = user.JobClass ?? '';
                    if (val !== current) {
                      onSchedulingProfileChange(user.AppUserKey, 'jobClass', val || null);
                    }
                  }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.4, pl: 1 } }}
                />
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{user.JobClass || '—'}</Typography>
              )}

              {/* Flags cluster: Assign · AI · Active */}
              <Box sx={{ ...clusterEdge, display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 0.5 }}>
                {/* Assignable */}
                {onToggleAssignable ? (
                  <Tooltip title={isAssignable ? 'Visible in assignment dropdowns — click to hide' : 'Hidden from dropdowns — click to show'}>
                    <IconButton size="small" onClick={() => onToggleAssignable(user.AppUserKey, isAssignable)}
                      sx={{ p: 0.25, color: isAssignable ? theme.palette.success.main : alpha(theme.palette.text.primary, 0.2) }}>
                      {isAssignable ? <CheckCircle sx={{ fontSize: 19 }} /> : <Cancel sx={{ fontSize: 19 }} />}
                    </IconButton>
                  </Tooltip>
                ) : (
                  isAssignable ? <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} /> : <Cancel sx={{ fontSize: 18, color: alpha(theme.palette.text.primary, 0.2) }} />
                )}

                {/* AI access */}
                {onToggleChatAccess ? (
                  <Tooltip title={hasChat ? 'Moftaki AI enabled — click to revoke' : 'No Moftaki AI access — click to grant'}>
                    <IconButton size="small" onClick={() => onToggleChatAccess(user.AppUserKey, hasChat)}
                      sx={{
                        borderRadius: '6px', p: 0.5,
                        backgroundColor: hasChat ? alpha(AI_ACCENT, 0.1) : 'transparent',
                        border: '1px solid', borderColor: hasChat ? alpha(AI_ACCENT, 0.3) : 'transparent',
                        opacity: hasChat ? 1 : 0.3, transition: 'all 0.2s',
                        '&:hover': { opacity: 1, backgroundColor: alpha(AI_ACCENT, 0.08) },
                      }}>
                      <MoftakiBotIcon size={18} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box sx={{ opacity: hasChat ? 1 : 0.3 }}><MoftakiBotIcon size={18} /></Box>
                )}

                {/* Active */}
                {onToggleStatus ? (
                  <Tooltip title={isActive ? 'Active — click to disable' : 'Disabled — click to activate'}>
                    <Switch size="small" checked={isActive}
                      onChange={() => onToggleStatus(user.AppUserKey, user.Status)} color="primary" />
                  </Tooltip>
                ) : (
                  <Chip label={user.Status} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                )}
              </Box>

              {/* Last login */}
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', fontSize: '0.7rem' }}>
                {formatDate(user.LastLoginDate)}
              </Typography>

              {/* Delete */}
              {onDelete ? (
                <Tooltip title="Remove user">
                  <IconButton size="small" onClick={() => onDelete(user)} sx={{
                    p: 0.25,
                    color: alpha(theme.palette.text.primary, 0.2),
                    '&:hover': { color: theme.palette.error.main },
                  }}>
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ) : <Box />}
            </Box>
          </Fade>
        );
      })}
    </Box>
  );
};
